
import { isDesktop } from '~/utils/screenSizes';
import ArrowIcon from '@/components/ui/icons/ArrowIcon.vue';
/**
 * A container that displays all of its elements linearly in a row, cutting
 * horizontal overflow, with navigation buttons to scroll, as well as a
 * scrollbar.
 *
 * @todo This component should, perhaps, have gradients on the sides where
 *       content is hidden.
 * @todo We should perhaps indicate that the left-right navigation buttons are
 *       at their bounds.
 */
export default {
  name: 'CardOverflowContainer',
  components: {
    ArrowIcon,
  },
  props: {
    /**
     * An optional icon added before the title
     */
    icon: {
      required: false,
      type: String,
      default: null,
    },
    /**
     * An optional title added to the top of the carousel.
     */
    title: {
      required: false,
      type: String,
      default: null,
    },
    /**
     * The scroll gait in pixels.
     */
    stepSize: {
      required: false,
      type: Number,
      default: 128,
    },
    /**
     * Enable navigation buttons.
     */
    withNavButtons: {
      required: false,
      type: Boolean,
      default: true,
    },
    /**
     * The scroll animation speed in pixels per frame. Set to 0 to disable
     * animations.
     */
    animationSpeed: {
      required: false,
      type: Number,
      default: 16,
    },
    arrowsTopPosition: {
      type: Number,
      default: 39,
    },
    titleClasses: {
      type: String,
      default: 'mb-2 text-fs-2 text-fw-3',
    },
    contentClasses: {
      type: String,
      required: false,
      default: 'gap-1 md:gap-2 px-4 md:px-0 pb-0',
    },
    /**
     * Whether to show the navigation arrow.
     * Will be displayed at the extreme right of the title container
     */
    showNavigationArrow: {
      type: Boolean,
      default: false,
    },
    displayType: {
      type: String,
      default: 'card',
      validator: (v) => ['card', 'round'].includes(v),
    },
  },
  data() {
    return {
      animation: { interval: null, target: 0 },
      shouldShowArrows: false,
      isScrolled: false,
      isFullyScrolled: false,
      mutationObserver: null,
    };
  },
  computed: {
    isDesktopSize() {
      return isDesktop();
    },
    isRoundDisplay() {
      return this.displayType === 'round';
    },
    isCardDisplay() {
      return this.displayType === 'card';
    },
  },
  mounted() {
    this.$nextTick(() => {
      requestAnimationFrame(() => this.isOverflown());
    });
    this.mutationObserver = new MutationObserver(() => {
      this.$nextTick(() => this.isOverflown());
    });
    this.mutationObserver.observe(this.$refs.carouselContainerContent, {
      childList: true,
      subtree: true,
    });
  },
  beforeUnmount() {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  },
  methods: {
    /**
     * Scrolls the carousel horizontally by the given amount.
     *
     * This implementation replaces the previous interval-based linear animation with a requestAnimationFrame-driven animation using an easing function.
     *
     * The goal of this remake is to provide a smoother and snappier scrolling experience
     * If a new scroll is requested while an animation is in progress, the current animation is interrupted and a new one starts from the current scroll position towards the updated target.
     *
     * @param {number} amount Horizontal distance (in pixels) to scroll.
     */
    scroll(amount) {
      const containerDOM = this.$refs.carouselContainerContent;

      // Apply the requested movement immediately when animations are disabled.
      if (this.animationSpeed === 0) {
        containerDOM.scrollLeft += amount;
        this.updateScrollState(containerDOM);
        return;
      }

      const maxScrollLeft = containerDOM.scrollWidth - containerDOM.clientWidth;

      // Always start the new animation from the currently rendered position (avoids jumpy jumps when interrupting current animation)
      const startPosition = containerDOM.scrollLeft;

      // If another animation is already running, accumulate the requested movement from its target so repeated clicks remain responsive
      // Small note on that behavior, repetead clicks don't increase the duration, while changing the target. At usage, this feels alright with the current duration, but keep this in mind if we tune the duration
      const targetBase =
        this.animation.frame !== null ? this.animation.target : startPosition;

      const targetPosition = Math.max(
        0,
        Math.min(targetBase + amount, maxScrollLeft)
      );

      const distance = targetPosition - startPosition;

      // Interrupt the current animation before starting its replacement.
      if (this.animation.frame !== null) {
        cancelAnimationFrame(this.animation.frame);
        this.animation.frame = null;
      }

      if (distance === 0) {
        this.animation.target = startPosition;
        this.updateScrollState(containerDOM);
        return;
      }

      const duration = 180;
      const startTime = performance.now();

      const easeOutCubic = (progress) => {
        return 1 - Math.pow(1 - progress, 3);
      };

      const animate = (currentTime) => {
        const elapsed = currentTime - startTime;
        // Limit progress to 1 because animation frames are not guaranteed to run exactly when the configured duration is reached
        const progress = Math.min(elapsed / duration, 1);
        const easedProgress = easeOutCubic(progress);

        containerDOM.scrollLeft = startPosition + distance * easedProgress;

        this.updateScrollState(containerDOM);

        if (progress < 1) {
          this.animation.frame = requestAnimationFrame(animate);
        } else {
          // Set the exact final position to avoid any small rounding errors
          containerDOM.scrollLeft = targetPosition;
          this.animation.frame = null;
          this.animation.target = targetPosition;

          this.updateScrollState(containerDOM);
        }
      };

      this.animation.target = targetPosition;
      this.animation.frame = requestAnimationFrame(animate);
    },

    updateScrollState(containerDOM) {
      this.isScrolled = containerDOM.scrollLeft > 0;

      this.isFullyScrolled =
        Math.abs(
          containerDOM.scrollLeft +
            containerDOM.clientWidth -
            containerDOM.scrollWidth
        ) < 2;
    },
    // Checking if we should display navigation arrows
    isOverflown() {
      const containerDOM = this.$refs.carouselContainerContent;
      if (!containerDOM) return;
      this.shouldShowArrows =
        containerDOM.scrollWidth > containerDOM.clientWidth;

      this.isScrolled = containerDOM.scrollLeft > 0;
      this.isFullyScrolled =
        Math.abs(
          containerDOM.scrollLeft +
            containerDOM.clientWidth -
            containerDOM.scrollWidth
        ) < 2;
    },
  },
};
